import React, { useEffect, useState } from 'react';
import { ChatCenteredIcon } from '@phosphor-icons/react';
import { formatTimeIndex } from '@/shared/functions';
interface TrackProps {
  currentTime: number;
  duration: number;
  handleTimeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTimelineClick: (e: React.MouseEvent<HTMLDivElement>, startTime?: number, updateMediaContext?: (field: string, value: any) => void, mediaItem?: any) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handleMouseLeave: () => void;
  hoverTime: number | null;
  filteredTranscript: any[];
  activeSegmentIndex: number;
  handleSegmentClick: (startTime: number) => void;
  showTranscriptMarkers: boolean;
  startTime?: number;
  endTime?: number;
  updateMediaContext?: (field: string, value: any) => void;
  mediaItem?: any;
  isPlaying: boolean;
  mediaRef?: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
  setCurrentTime?: (time: number) => void;
  isEditingMediaContext?: boolean;
}
interface IndicatorProps {
  type: 'start' | 'end';
  position: number;
  duration: number;
  isEditing?: boolean;
  isDragging: 'start' | 'end' | null;
  onMouseDown: (e: React.MouseEvent, type: 'start' | 'end') => void;
}
const TimeIndicator: React.FC<IndicatorProps> = ({
  type,
  position,
  duration,
  isEditing,
  isDragging,
  onMouseDown
}) => {
  const isActive = isDragging === type;
  return <div className={`absolute box-border appearance-none ${type === 'start' ? 'bg-green-600' : 'bg-red-600'} ${isEditing ? 'w-[10px] h-[17px] rounded-full top-[-5px]' : 'w-[5px] h-1.5 top-0.5 border-1 border-gray-600 border-solid'}`} style={{
    left: `calc(${position / duration * 100}%)`,
    transform: 'translateX(-50%)',
    zIndex: isActive ? 35 : 30,
    cursor: isEditing ? 'grab' : 'default',
    pointerEvents: isEditing ? 'auto' : 'none',
    border: isActive ? '2px solid white' : '1px solid transparent',
    boxShadow: isActive ? '0 0 5px rgba(255, 255, 255, 0.5)' : 'none'
  }} onMouseDown={e => onMouseDown(e, type)} data-sentry-component="TimeIndicator" data-sentry-source-file="track.tsx" />;
};
const Track: React.FC<TrackProps> = ({
  currentTime,
  duration,
  handleTimeChange,
  handleTimelineClick,
  handleMouseMove,
  handleMouseLeave,
  hoverTime,
  filteredTranscript,
  activeSegmentIndex,
  handleSegmentClick,
  showTranscriptMarkers,
  startTime,
  endTime,
  updateMediaContext,
  mediaItem,
  isPlaying,
  mediaRef,
  setCurrentTime,
  isEditingMediaContext
}) => {
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(showTranscriptMarkers);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [selectedIndicator, setSelectedIndicator] = useState<'start' | 'end' | null>(null);
  const [startOrEnd, setStartOrEnd] = useState<'start' | 'end' | null>(null);
  useEffect(() => {
    setShowTranscriptMarkersState(showTranscriptMarkers);
  }, [showTranscriptMarkers]);
  const handleDragStart = (e: React.MouseEvent, type: 'start' | 'end') => {
    if (!isEditingMediaContext) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.closest('.box-border.relative.h-1\\.5')?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (type === 'start' && newTime < (endTime || duration) && updateMediaContext) {
      setStartOrEnd('start');
      updateMediaContext('startTime', Number(newTime.toFixed(2)));
    } else if (type === 'end' && newTime > (startTime || 0) && updateMediaContext) {
      setStartOrEnd('end');
      updateMediaContext('endTime', Number(newTime.toFixed(2)));
    }
    setIsDragging(type);
  };
  const handleScrubStart = (e: React.MouseEvent) => {
    if (isEditingMediaContext) return;
    e.preventDefault();
    e.stopPropagation();

    // Calculate position relative to the timeline container, not the scrubber dot
    const timelineElement = e.currentTarget.closest('.track-container');
    if (!timelineElement) return;
    const rect = timelineElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    // Set scrub time first to prevent position flash
    setScrubTime(newTime);
    setIsScrubbing(true);
    if (setCurrentTime) {
      setCurrentTime(newTime);
    }
  };
  const handleScrubMove = (e: MouseEvent) => {
    if (!isScrubbing) return;
    const timelineElement = document.querySelector('.track-container');
    if (!timelineElement) return;
    const rect = timelineElement.getBoundingClientRect();
    // Clamp the x position to the track bounds
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    // Always update scrub time and current time
    setScrubTime(newTime);
    if (setCurrentTime) {
      setCurrentTime(newTime);
    }
  };
  const handleScrubEnd = () => {
    // Ensure the final position is saved before ending the scrub
    if (isScrubbing && setCurrentTime) {
      // Use the last known scrub time, or fall back to current time
      const finalTime = scrubTime !== null ? scrubTime : currentTime;
      setCurrentTime(finalTime);
    }
    setIsScrubbing(false);
    setScrubTime(null);
  };
  useEffect(() => {
    if (!isEditingMediaContext) {
      setSelectedIndicator(null);
    }
  }, [isEditingMediaContext]);
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width * 100;
    const newTime = percentage / 100 * duration;
    if (isEditingMediaContext && selectedIndicator && updateMediaContext) {
      if (selectedIndicator === 'start' && newTime < (endTime || duration)) {
        updateMediaContext('startTime', Number(newTime.toFixed(2)));
      } else if (selectedIndicator === 'end' && newTime > (startTime || 0)) {
        updateMediaContext('endTime', Number(newTime.toFixed(2)));
      }
      return;
    }
    handleTimelineClick(e, startTime, updateMediaContext, mediaItem);
  };
  const handleDragMove = (e: MouseEvent) => {
    if (!isDragging || !updateMediaContext || !isEditingMediaContext) return;
    const timelineElement = document.querySelector('.box-border.relative.h-1\\.5');
    if (!timelineElement) return;
    const rect = timelineElement.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (isDragging === 'start') {
      setStartOrEnd('start');
      if (newTime < (mediaItem.mediaContext?.endTime || duration)) {
        updateMediaContext('startTime', Number(newTime.toFixed(2)));
      }
    } else if (isDragging === 'end') {
      setStartOrEnd('end');
      if (newTime > (mediaItem.mediaContext?.startTime || 0)) {
        updateMediaContext('endTime', Number(newTime.toFixed(2)));
      }
    }
  };
  const handleDragEnd = () => {
    setIsDragging(null);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!updateMediaContext || !isEditingMediaContext) return;
    e.preventDefault();
    e.stopPropagation();
    const currentTime = startOrEnd === 'start' ? mediaItem.mediaContext?.startTime || 0 : mediaItem.mediaContext?.endTime || duration;
    let newTime = currentTime;
    if (e.key === 'ArrowLeft') {
      newTime = Number((currentTime - 0.1).toFixed(2));
      if (newTime < 0) {
        newTime = 0;
      }
    } else if (e.key === 'ArrowRight') {
      newTime = Number((currentTime + 0.1).toFixed(2));
      if (newTime > duration) {
        newTime = duration;
      }
    }
    updateMediaContext(startOrEnd === 'start' ? 'startTime' : 'endTime', newTime);
  };
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging]);
  useEffect(() => {
    if (isScrubbing) {
      window.addEventListener('mousemove', handleScrubMove);
      window.addEventListener('mouseup', handleScrubEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleScrubMove);
      window.removeEventListener('mouseup', handleScrubEnd);
    };
  }, [isScrubbing]);

  // Use scrubTime for position when scrubbing, otherwise use currentTime
  const displayTime = isScrubbing && scrubTime !== null ? scrubTime : currentTime;
  return <div role="slider" tabIndex={0} aria-label="Media timeline" aria-valuemin={0} aria-valuemax={duration} aria-valuenow={displayTime} aria-valuetext={`${Math.floor(displayTime / 60)}:${String(Math.floor(displayTime % 60)).padStart(2, '0')}`} onKeyDown={onKeyDown} className="track-container relative box-border h-1.5 min-h-1.5 z-40 mt-5 mr-3.5 mb-6 ml-3 cursor-pointer focus:outline focus:outline-2 focus:outline-white rounded" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onClick={handleTrackClick} data-sentry-component="Track" data-sentry-source-file="track.tsx">

      {/* Timeline Background */}
      <div className="absolute box-border top-0.5 left-0.5 h-full bg-gray-600 rounded-full z-0" style={{
      width: '100%'
    }}></div>

      {/* Current Time Indicator */}
      {(!isEditingMediaContext || isPlaying) && <>
          {/* Progress Track */}
          <div className="absolute box-border top-0.5 left-0.5 h-full bg-gradient-to-r from-white/50 via-white/70 to-white/80 rounded-l-full z-10 border-b-1 border-gray-800 border-solid" style={{
        width: `calc(${displayTime / duration * 100}%)`,
        display: startTime !== undefined || endTime !== undefined ? 'none' : 'block'
      }} />
          {/* Current Time Indicator Dot */}
          <div className="absolute top-[-4px] right-[-4px] w-[17px] h-[17px] appearance-none cursor-grab bg-gradient-to-br from-white to-gray-500 rounded-full border-r-1 border-b-1 border-gray-800 border-solid" style={{
        left: `calc(${displayTime / duration * 100}%)`,
        zIndex: isEditingMediaContext ? 15 : 40
      }} onMouseDown={handleScrubStart} />
        </>}

      {/* Start and End Time Range */}
      {(isEditingMediaContext || startTime !== undefined && endTime !== undefined) && <>
          {/* Light gray track between indicators */}
          <div className="absolute box-border top-0.5 h-full bg-gray-400" style={{
        left: `calc(${(startTime || 0) / duration * 100}%)`,
        width: `calc(${((endTime || duration) - (startTime || 0)) / duration * 100}%)`,
        zIndex: 5
      }} />
          
          <TimeIndicator type="start" position={isEditingMediaContext ? mediaItem.mediaContext.startTime || 0 : startTime || 0} duration={duration} isEditing={isEditingMediaContext} isDragging={isDragging} onMouseDown={handleDragStart} />

          <TimeIndicator type="end" position={isEditingMediaContext ? mediaItem.mediaContext.endTime || duration : endTime || duration} duration={duration} isEditing={isEditingMediaContext} isDragging={isDragging} onMouseDown={handleDragStart} />
        </>}

      {/* Timeline Input */}
      <input type="range" value={currentTime} min={0} max={duration || 0} step="0.01" onChange={handleTimeChange} className="absolute inset-0 w-full h-full appearance-none cursor-pointer bg-transparent rounded-full outline-none z-20" />

      {/* Hover Time Indicator */}
      {hoverTime !== null && hoverTime >= 0 && hoverTime <= duration && <div className={`absolute flex items-center justify-center text-white bg-gray-900/20 px-1 py-[0.5px] rounded border-1 border-white/20 border-solid ${isEditingMediaContext ? 'text-[14px]' : 'text-[10px]'}`} style={{
      top: isEditingMediaContext ? '-20px' : '14px',
      left: `${hoverTime / duration * 100}%`,
      transform: 'translateX(-50%)'
    }}>
            <span className="cursor-pointer">{formatTimeIndex(hoverTime.toString())}</span>
          </div>}

      {/* Transcript Markers */}
      {showTranscriptMarkersState && !isEditingMediaContext && filteredTranscript.map((item, index) => <div key={index} className={`box-border absolute h-[8px] w-0.5 cursor-pointer mx-1 mt-[-11px] ${index === activeSegmentIndex ? 'bg-white text-white' : item.start < currentTime ? 'bg-white/50 text-white/65 hover:bg-white hover:text-white' : 'bg-yellow-400/60 text-yellow-400/60 hover:bg-white hover:text-white'}`} style={{
      left: `${item.start / duration * 100}%`,
      transform: 'translateX(-50%) translateY(50%)',
      zIndex: 5
    }} onClick={() => handleSegmentClick(item.start)}>
          <ChatCenteredIcon weight="fill" size={12} className="relative top-[-14px] left-[-5px]" />
        </div>)}
    </div>;
};
export default Track;